import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router";
import type { Route } from "./+types/root";
import {
  jsonLdString,
  softwareAppJsonLd,
  websiteJsonLd,
} from "~/lib/seo/jsonld";
import "./app.css";

// Pre-serialize once at module load — these payloads are static, so there's no
// reason to stringify them per request. Both entity nodes (the WebSite and the
// SoftwareApplication) ship on every page as a JSON-LD graph array.
const SITE_JSONLD = jsonLdString([websiteJsonLd(), softwareAppJsonLd()]);

export const links: Route.LinksFunction = () => [
  { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
  { rel: "alternate icon", href: "/favicon.png", type: "image/png" },
  { rel: "apple-touch-icon", href: "/favicon.png" },
];

export const meta: Route.MetaFunction = () => [
  { title: "Emulator Arcade — Retro games inside Discord" },
  {
    name: "description",
    content:
      "Play NES, SNES, Game Boy, Genesis and a dozen other retro consoles right inside a Discord voice channel. Bring your own ROM, hit play, no install required.",
  },
  { name: "robots", content: "index, follow" },
  { name: "theme-color", content: "#161719" },
  { property: "og:locale", content: "en_US" },
  { property: "og:site_name", content: "Emulator Arcade" },
  { property: "og:type", content: "website" },
];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {/* WebSite + SoftwareApplication JSON-LD on every page. Rendered in
            Layout (not via the meta function) so child routes can declare their
            own meta descriptors without dropping the entity nodes — child meta
            replaces parent meta in React Router. */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: SITE_JSONLD }}
        />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (import.meta.env.DEV && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-4">
      <div className="text-center max-w-2xl">
        <h1 className="text-4xl font-bold">{message}</h1>
        <p className="mt-2 text-slate-600">{details}</p>
        {import.meta.env.DEV && stack && (
          <pre className="mt-4 p-4 bg-slate-100 rounded-lg text-left text-sm overflow-auto">
            {stack}
          </pre>
        )}
      </div>
    </main>
  );
}
